import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Search, Filter, ChevronDown } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AuctionCard from '@/components/AuctionCard';
import { BreedSelector } from '@/components/BreedSelector';
import { LocationAutocomplete } from '@/components/dashboard/LocationAutocomplete';
import { LocationRangeSlider } from '@/components/LocationRangeSlider';
import { useAuctions } from '@/hooks/useAuctions';
import BreedingValueFilters, { type AppliedFilters } from '@/components/marketplace/BreedingValueFilters';

const Auctions = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBreed, setSelectedBreed] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationCoordinates, setLocationCoordinates] = useState<[number, number] | null>(null);
  const [rangeKm, setRangeKm] = useState(50);
  const [selectedType, setSelectedType] = useState('all');
  const [locationType, setLocationType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const emptyAppliedFilters: AppliedFilters = { priceMin: '', priceMax: '', breedingValues: {} };
  const [appliedAdvancedFilters, setAppliedAdvancedFilters] = useState<AppliedFilters>(emptyAppliedFilters);

  const auctionsPerPage = 10;

  const { data, isLoading, error } = useAuctions({
    page: currentPage,
    limit: auctionsPerPage,
  });

  const auctions = data?.auctions || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / auctionsPerPage);

  useEffect(() => {
    let previousIsDesktop: boolean | null = null;
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      if (previousIsDesktop !== null && previousIsDesktop !== desktop) {
        if (desktop) setIsFiltersOpen(true);
      } else if (previousIsDesktop === null) {
        setIsFiltersOpen(desktop);
      }
      setIsDesktop(desktop);
      previousIsDesktop = desktop;
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBreed, selectedLocation, locationCoordinates, rangeKm, selectedType, appliedAdvancedFilters]);

  const types = [
    { value: 'all', label: 'All' },
    { value: 'bull', label: 'Bulls' },
    { value: 'cow', label: 'Cows' },
  ];

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBreed('');
    setSelectedLocation('');
    setLocationCoordinates(null);
    setLocationType(null);
    setRangeKm(50);
    setSelectedType('all');
    setCurrentPage(1);
    setAppliedAdvancedFilters(emptyAppliedFilters);
  };

  const handleLocationChange = (location: string, coordinates?: [number, number], locType?: string) => {
    setSelectedLocation(location);
    setLocationCoordinates(coordinates || null);
    setLocationType(locType || null);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-card rounded-lg shadow-sm">
              <Collapsible open={isFiltersOpen}>
                <div className="flex items-center justify-between w-full p-6">
                  <div className="flex items-center">
                    <Filter className="w-5 h-5 mr-2" />
                    <h2 className="text-lg font-semibold">Filters</h2>
                  </div>
                  {!isDesktop && (
                    <CollapsibleTrigger asChild>
                      <button
                        className="p-1 hover:bg-muted rounded"
                        onClick={() => setIsFiltersOpen(!isFiltersOpen)}
                        type="button"
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </CollapsibleTrigger>
                  )}
                </div>

                <CollapsibleContent className="lg:block">
                  <div className="px-6 pb-6 space-y-6">
                    <div>
                      <label className="block text-sm font-medium mb-2">Search</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                        <Input
                          placeholder="Search auctions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Breed</label>
                      <BreedSelector
                        value={selectedBreed}
                        onValueChange={setSelectedBreed}
                        placeholder="All breeds"
                        className="w-full"
                      />
                      {selectedBreed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedBreed('')}
                          className="mt-1 h-6 px-2 text-xs"
                        >
                          Clear breed
                        </Button>
                      )}
                    </div>

                    <div>
                      <LocationAutocomplete
                        value={selectedLocation}
                        onChange={handleLocationChange}
                        placeholder="Search for a location..."
                      />
                      {selectedLocation && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleLocationChange('', undefined)}
                          className="mt-1 h-6 px-2 text-xs"
                        >
                          Clear location
                        </Button>
                      )}
                    </div>

                    {selectedLocation && locationCoordinates && (locationType === 'place' || locationType === 'locality') && (
                      <div>
                        <LocationRangeSlider
                          value={rangeKm}
                          onChange={setRangeKm}
                        />
                      </div>
                    )}

                    <BreedingValueFilters
                      appliedFilters={appliedAdvancedFilters}
                      onApply={(filters) => setAppliedAdvancedFilters(filters)}
                      onClear={() => setAppliedAdvancedFilters(emptyAppliedFilters)}
                    />

                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={clearFilters}
                    >
                      Clear All Filters
                    </Button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>

          {/* Auctions Section */}
          <div className="lg:w-3/4">
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold text-foreground">Auctions</h1>
              <p className="text-muted-foreground">{totalCount} auctions found</p>
            </div>

            <Tabs value={selectedType} onValueChange={setSelectedType} className="mb-8">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                {types.map((type) => (
                  <TabsTrigger
                    key={type.value}
                    value={type.value}
                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                  >
                    {type.label}
                  </TabsTrigger>
                ))}
              </TabsList>

              {types.map((type) => (
                <TabsContent key={type.value} value={type.value} className="mt-0">
                  {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">Unable to load auctions at the moment.</p>
                    </div>
                  ) : auctions.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-muted-foreground">No auctions found.</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-6">
                        {auctions.map((auction: any) => (
                          <AuctionCard
                            key={auction.id}
                            auction={auction}
                            searchTerm={searchTerm}
                            selectedBreed={selectedBreed}
                            selectedType={selectedType}
                            selectedLocation={selectedLocation}
                            locationCoordinates={locationCoordinates}
                            rangeKm={rangeKm}
                            locationType={locationType}
                            appliedAdvancedFilters={appliedAdvancedFilters}
                          />
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div className="mt-8 flex justify-center">
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  className={currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) pageNum = i + 1;
                                else if (currentPage <= 3) pageNum = i + 1;
                                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                else pageNum = currentPage - 2 + i;
                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationLink
                                      onClick={() => handlePageChange(pageNum)}
                                      isActive={currentPage === pageNum}
                                      className="cursor-pointer"
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              })}
                              <PaginationItem>
                                <PaginationNext
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  className={currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        </div>
                      )}
                    </>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Auctions;
